import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _styled from "styled-components";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/* eslint-disable @typescript-eslint/no-unused-vars */

/* eslint-disable no-unused-vars */


import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import styled from 'styled-components';
import { Transition } from 'react-transition-group';
import { reflow } from "../../utils/reflow";
import { SharedHoverContext } from "./SharedHoverContext";
import { theme } from "../../theme";
import { Box } from "../Box/Box";
var defaultState = {
  target: null,
  bounds: null,
  lockedBounds: null
};

function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultState;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  switch (action.type) {
    case 'ENTER_TARGET':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          bounds: action.payload.bounds,
          target: action.payload.target
        });
      }

    case 'EXIT_TARGET':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          target: null
        });
      }

    case 'LOCK_TARGET':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          lockedBounds: action.payload.bounds
        });
      }

    case 'UNLOCK_TARGET':
      {
        if (state.lockedBounds === action.payload.bounds) {
          return _objectSpread(_objectSpread({}, state), {}, {
            lockedBounds: null
          });
        }

        return state;
      }

    default:
      throw new Error("Unexpected action. You aren't using typescript, are you?");
  }
}

var HOVER_INDICATOR_FADE_DURATION = 150;

var _StyledBox = styled(Box).withConfig({
  displayName: "SharedHover___StyledBox",
  componentId: "vapor__pqxykz-0"
})(["", ""], function (p) {
  return p._css;
});

export function SharedHover(_ref) {
  var children = _ref.children,
      _ref$mouseLeaveTimeou = _ref.mouseLeaveTimeout,
      mouseLeaveTimeout = _ref$mouseLeaveTimeou === void 0 ? 20 : _ref$mouseLeaveTimeou,
      _ref$transitionDurati = _ref.transitionDuration,
      transitionDuration = _ref$transitionDurati === void 0 ? 100 : _ref$transitionDurati,
      UNSAFE_CSS = _ref.UNSAFE_CSS,
      UNSAFE_STYLE = _ref.UNSAFE_STYLE,
      className = _ref.className,
      style = _ref.style,
      _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'secondary' : _ref$color,
      _ref$hoverRadius = _ref.hoverRadius,
      hoverRadius = _ref$hoverRadius === void 0 ? 6 : _ref$hoverRadius,
      other = _objectWithoutProperties(_ref, ["children", "mouseLeaveTimeout", "transitionDuration", "UNSAFE_CSS", "UNSAFE_STYLE", "className", "style", "color", "hoverRadius"]);

  var _useReducer = useReducer(reducer, defaultState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      _useReducer2$ = _useReducer2[0],
      target = _useReducer2$.target,
      bounds = _useReducer2$.bounds,
      lockedBounds = _useReducer2$.lockedBounds,
      dispatch = _useReducer2[1];

  var timerRef = useRef(null);
  var boundsRef = useRef(null);
  var lastDefinedTargetRef = useRef(null);
  useEffect(function () {
    // Store the last remaining defined value. We need
    // to hold this to ensure when we unmount our slider
    // we keep the hover indicator in the correct position.
    if (target !== null && !lockedBounds) {
      lastDefinedTargetRef.current = target;
    }
  }, [lockedBounds, target]);
  /**
   * Measure our bounds each time a hover occurs.
   *
   * An alternative would be to use
   * a resize observer, but I think this is actually more efficient
   * and less likely to cause issues. It is dependent upon the container
   * _not_ changing size during hover, though. But we shouldn't be using
   * this in highly dynamic cases anyway.
   */

  var onEnterTarget = useCallback(function (target) {
    var bounds = boundsRef.current.getBoundingClientRect();
    dispatch({
      type: 'ENTER_TARGET',
      payload: {
        bounds: bounds,
        target: target
      }
    });
    window.clearTimeout(timerRef.current);
  }, []);
  var onLeaveTarget = useCallback(function () {
    timerRef.current = window.setTimeout(function () {
      return dispatch({
        type: 'EXIT_TARGET'
      });
    }, mouseLeaveTimeout);
  }, [mouseLeaveTimeout]);
  var onLockTarget = useCallback(function (bounds) {
    dispatch({
      type: 'LOCK_TARGET',
      payload: {
        bounds: bounds
      }
    });
  }, []);
  var onUnlockTarget = useCallback(function (bounds) {
    dispatch({
      type: 'UNLOCK_TARGET',
      payload: {
        bounds: bounds
      }
    });
  }, []);
  var context = useMemo(function () {
    return {
      onEnterTarget: onEnterTarget,
      onLeaveTarget: onLeaveTarget,
      onLockTarget: onLockTarget,
      onUnlockTarget: onUnlockTarget
    };
  }, [onEnterTarget, onLeaveTarget, onLockTarget, onUnlockTarget]);
  var showHoverIndicator = target !== null || lockedBounds; // When unmounting, we need to position it in its last viable state.

  var actualTarget = showHoverIndicator ? target : lastDefinedTargetRef.current;

  if (lockedBounds) {
    actualTarget = lockedBounds;
  }
  /**
   * Determine the position of the hover indicator given the target position,
   * and the container position.
   */


  var hoverStyle = useMemo(function () {
    if (!actualTarget || !bounds) {
      return null;
    }

    var y = actualTarget.top - bounds.top;
    var x = actualTarget.left - bounds.left;
    var w = actualTarget.width;
    var h = actualTarget.height;
    return {
      borderRadius: "".concat(hoverRadius, "px"),
      width: "".concat(w, "px"),
      height: "".concat(h, "px"),
      transform: "translate(".concat(x, "px, ").concat(y, "px)")
    };
  }, [actualTarget, bounds, hoverRadius]);
  return /*#__PURE__*/React.createElement(SharedHoverContext.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement(_StyledBox, _extends({
    ref: boundsRef,
    style: UNSAFE_STYLE,
    position: "relative"
  }, other, {
    _css: UNSAFE_CSS
  }), /*#__PURE__*/React.createElement(Transition, {
    timeout: HOVER_INDICATOR_FADE_DURATION,
    mountOnEnter: true,
    unmountOnExit: true,
    onEnter: function onEnter(el) {
      return reflow(el);
    },
    in: !!showHoverIndicator
  }, function (state) {
    return /*#__PURE__*/React.createElement(HoverIndicator, {
      $borderRadius: hoverRadius,
      $transitionDuration: transitionDuration,
      $color: color,
      "data-state": state,
      style: hoverStyle
    });
  }), children));
}
var HoverIndicator = styled.div.withConfig({
  displayName: "SharedHover__HoverIndicator",
  componentId: "vapor__pqxykz-1"
})(["position:absolute;border-radius:", ";pointer-events:none;left:0;top:0;background:", ";transform-origin:top left;transition:transform ", "ms linear,width ", "ms linear,height ", "ms linear,opacity ", "ms linear,border-radius 75ms linear;opacity:0;&[data-state='entering'],&[data-state='entered']{opacity:1;}&[data-state='exiting']{opacity:0;}"], function (p) {
  return theme.radii[p.$borderRadius];
}, function (p) {
  return p.$color === 'secondary' ? theme.colors.background.interactive.hover : theme.colors.palette.whiteA10;
}, function (p) {
  return p.$transitionDuration;
}, function (p) {
  return p.$transitionDuration;
}, function (p) {
  return p.$transitionDuration;
}, HOVER_INDICATOR_FADE_DURATION);